import { lazy, useEffect, type ReactElement, type ReactNode } from "react";
import { Navigate, Route } from "react-router-dom";
import { ProtectedRoute } from "@/shared/auth/ProtectedRoute";
import { SettingsLayout } from "@/app/components/SettingsLayout";
import { BRANDING } from "@/shared/config/branding";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";

// App subdomain has no landing page — bounce unmatched paths to the
// marketing site so /, /about, and any stray marketing links keep working.
function MarketingRedirect() {
  useEffect(() => {
    const target = new URL(
      window.location.pathname + window.location.search,
      BRANDING.siteUrl,
    );
    window.location.replace(target.toString());
  }, []);
  return <LoadingSpinner />;
}

const ProposalCreate = lazy(() =>
  import("@/app/pages/ProposalCreate").then((m) => ({
    default: m.ProposalCreate,
  })),
);
const Login = lazy(() =>
  import("@/app/pages/Login").then((m) => ({ default: m.Login })),
);
const Signup = lazy(() =>
  import("@/app/pages/Signup").then((m) => ({ default: m.Signup })),
);
const Dashboard = lazy(() =>
  import("@/app/pages/Dashboard").then((m) => ({ default: m.Dashboard })),
);
const AuthCallback = lazy(() =>
  import("@/app/pages/AuthCallback").then((m) => ({
    default: m.AuthCallback,
  })),
);
const ProposalEditor = lazy(() =>
  import("@/app/pages/ProposalEditor").then((m) => ({
    default: m.ProposalEditor,
  })),
);
const ProposalAnalytics = lazy(() =>
  import("@/app/pages/ProposalAnalytics").then((m) => ({
    default: m.ProposalAnalytics,
  })),
);
const PublicProposal = lazy(() =>
  import("@/app/pages/PublicProposal").then((m) => ({
    default: m.PublicProposal,
  })),
);
const DraftPreview = lazy(() =>
  import("@/app/pages/DraftPreview").then((m) => ({
    default: m.DraftPreview,
  })),
);
const PublicReview = lazy(() =>
  import("@/app/pages/PublicReview").then((m) => ({
    default: m.PublicReview,
  })),
);
const SampleProposal = lazy(() =>
  import("@/app/pages/SampleProposal").then((m) => ({
    default: m.SampleProposal,
  })),
);
const SettingsBusiness = lazy(() =>
  import("@/app/pages/SettingsBusiness").then((m) => ({
    default: m.SettingsBusiness,
  })),
);
const SettingsBrand = lazy(() =>
  import("@/app/pages/SettingsBrand").then((m) => ({
    default: m.SettingsBrand,
  })),
);
const SettingsBilling = lazy(() =>
  import("@/app/pages/SettingsBilling").then((m) => ({
    default: m.SettingsBilling,
  })),
);
const SettingsSupport = lazy(() =>
  import("@/app/pages/SettingsSupport").then((m) => ({
    default: m.SettingsSupport,
  })),
);

// Local-only preview of the ChatGPT card in every look (PK-285). Dev builds
// only: in production the condition is false at build time, so the route is
// never registered and the page is never bundled.
const CardPreview = import.meta.env.DEV
  ? lazy(() =>
      import("@/app/pages/dev/CardPreview").then((m) => ({
        default: m.CardPreview,
      })),
    )
  : null;

// Local-only check of every brand font in every look (PK-295), dev builds only
// like the card preview above.
const FontPreview = import.meta.env.DEV
  ? lazy(() =>
      import("@/app/pages/dev/FontPreview").then((m) => ({
        default: m.FontPreview,
      })),
    )
  : null;

interface AppRouteConfig {
  path: string;
  element: ReactElement;
  requiresAuth?: boolean;
}

const appRoutes: AppRouteConfig[] = [
  { path: "/p/:token", element: <PublicProposal /> },
  { path: "/preview/:token", element: <DraftPreview /> },
  { path: "/r/:token", element: <PublicReview /> },
  { path: "/sample/:slug", element: <SampleProposal /> },
  { path: "/login", element: <Login /> },
  { path: "/signup", element: <Signup /> },
  { path: "/auth/callback", element: <AuthCallback /> },
  { path: "/create", element: <ProposalCreate />, requiresAuth: true },
  { path: "/dashboard", element: <Dashboard />, requiresAuth: true },
  {
    path: "/proposals/:id/edit",
    element: <ProposalEditor />,
    requiresAuth: true,
  },
  {
    path: "/proposals/:id/analytics",
    element: <ProposalAnalytics />,
    requiresAuth: true,
  },
  ...(CardPreview ? [{ path: "/dev/card", element: <CardPreview /> }] : []),
  ...(FontPreview ? [{ path: "/dev/fonts", element: <FontPreview /> }] : []),
];

function withProtection(element: ReactNode, requiresAuth?: boolean) {
  return requiresAuth ? <ProtectedRoute>{element}</ProtectedRoute> : element;
}

export function renderAppRoutes() {
  return (
    <>
      {appRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={withProtection(route.element, route.requiresAuth)}
        />
      ))}

      <Route
        path="/settings"
        element={withProtection(<SettingsLayout />, true)}
      >
        <Route index element={<Navigate to="business" replace />} />
        <Route path="business" element={<SettingsBusiness />} />
        <Route path="brand" element={<SettingsBrand />} />
        {/* /settings/profile predates the business/brand split; old
            bookmarks land on Business. */}
        <Route
          path="profile"
          element={<Navigate to="/settings/business" replace />}
        />
        <Route path="billing" element={<SettingsBilling />} />
        <Route path="support" element={<SettingsSupport />} />
      </Route>

      {/* Temporary: send app-root to the dashboard rather than bouncing
          to marketing. Stops the disorienting cross-domain redirect in dev
          and for logged-in users who land on the bare app URL. Revisit if
          we ever want a genuine app landing page. */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<MarketingRedirect />} />
    </>
  );
}
